import React, { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { Typography, Box, FormHelperText, Button } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CustomButton from "components/Button";
import DialogComponent from "components/DialogComponent";
import { useUploadFileMutation } from "store/slices/apis/dmsSlice";
import { useDispatch } from "react-redux";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";

const SUPPORTED_FORMATS = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const EXECUTABLE_EXTENSIONS = [
    ".exe", ".msi", ".bat", ".cmd", ".com", ".scr", ".pif", // Windows
    ".php", ".js", ".jsp", ".asp", ".aspx", ".py", ".rb", ".sh", ".ts", // Scripts
    ".dll", ".sys", ".pdb", ".jar", ".app", ".dmg", // Libraries & Mac
    ".zip"
];

const validationSchema = yup.object({
    file: yup
        .mixed()
        .required("A file is required")
        .test(
            "fileType",
            "Executable or script files are not allowed for security reasons",
            (value) => {
                if (!value) return true;
                const fileName = value.name.toLowerCase();
                // Returns true if the file does NOT end with any forbidden extension
                return !EXECUTABLE_EXTENSIONS.some(ext => fileName.endsWith(ext));
            }
        ),
});

const UploadFile = ({ open, handleClose, files, setIsFileNameExist, onUpload, setPendingFile, isLoading }) => {
    //const [uploadFile, { isLoading }] = useUploadFileMutation();
    const [isDragging, setIsDragging] = useState(false);
    const dispatch = useDispatch();
    const formik = useFormik({
        initialValues: { file: null },
        validationSchema: validationSchema,
        onSubmit: async (values, { resetForm }) => {
            // try {
            //     const formData = new FormData();
            //     formData.append("FolderId", selectedFolderId);
            //     formData.append("File", values.file);
            //     const isFilesAlreadyExist = files.some(file => file.fileName === values.file.fileName);
            //     console.log("Files", files, values)
            //     if (isFilesAlreadyExist) {
            //         setIsFileNameExist(true)
            //         return
            //     }

            //     const result = await uploadFile(formData).unwrap();
            //     if (result?.status === "Success") {
            //         openNotification("File Uploaded Successfully", "success")
            //         //onSuccess();
            //         resetForm();
            //         handleClose();
            //     }

            // } catch (error) {
            //     openNotification(error?.data?.message || "File Upload Failed")
            //     console.error("Upload failed:", error);
            // }
            const isFilesAlreadyExist = files.some(file => file?.fileName?.toLowerCase() === values?.file?.name?.toLowerCase());

            if (isFilesAlreadyExist) {
                setPendingFile({ file: values.file, resetForm });
                setIsFileNameExist(true);
            } else {
                onUpload(values.file, resetForm);
            }
        },
    });

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        // if (droppedFile && SUPPORTED_FORMATS.includes(droppedFile.type)) {
        //     formik.setFieldValue("file", droppedFile);
        // } else {
        //     alert("This file type is not supported.");
        // }
        if (droppedFile) {
            const fileName = droppedFile.name.toLowerCase();
            const isForbidden = EXECUTABLE_EXTENSIONS.some(ext => fileName.endsWith(ext));

            if (!isForbidden) {
                formik.setFieldValue("file", droppedFile);
            } else {
                alert("This file type is blocked for security reasons.");
            }
        }
    };

    const openNotification = (msg, type = "error", duration = 5000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    return (
        <DialogComponent title="Upload File" open={open} handleClose={handleClose}>
            <form onSubmit={formik.handleSubmit}>
                <Box sx={{ p: 1 }}>
                    <Box
                        sx={{
                            border: "2px dashed #E5E7EB",
                            borderRadius: "3px",
                            p: 5,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            borderColor: isDragging ? "#00344C" : "#E5E7EB",
                            borderStyle: isDragging ? "solid" : "dashed",
                            backgroundColor: isDragging
                                ? "rgba(0, 52, 76, 0.08)"
                                : formik.values.file
                                    ? "rgba(0, 52, 76, 0.04)"
                                    : "transparent",
                            "&:hover": {
                                borderColor: "#00344C",
                                backgroundColor: "rgba(0, 52, 76, 0.02)",
                            },
                        }}
                        component="label"
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <input
                            hidden
                            type="file"
                            onChange={(event) => {
                                formik.setFieldValue("file", event.currentTarget.files[0]);
                            }}
                            accept="*"
                        />
                        <CloudUploadIcon
                            sx={{ fontSize: 48, color: "#00344C", opacity: 0.7, mb: 2 }}
                        />
                        <Typography
                            variant="body1"
                            sx={{ fontFamily: "Outfit", fontWeight: 500, color: "#374151" }}
                        >
                            {formik.values.file
                                ? formik.values.file.name
                                : "Click to select or drag and drop file"}
                        </Typography>
                        {formik.values.file && (
                            <Typography variant="caption" sx={{ mt: 1, color: "#6B7280" }}>
                                {(formik.values.file.size / 1024).toFixed(2)} KB
                            </Typography>
                        )}
                    </Box>

                    {formik.touched.file && formik.errors.file && (
                        <FormHelperText error sx={{ mt: 1, textAlign: "center" }}>
                            {formik.errors.file}
                        </FormHelperText>
                    )}
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1.5,
                        mt: 3,
                        pt: 2,
                        borderTop: "1px solid #F3F4F6",
                    }}
                >
                    {/* <CustomButton
                        label="Cancel"
                        onClick={handleClose}
                        variant="outlined"
                        sx={{
                            backgroundColor: "#F9FAFB",
                            color: "#4B5563",
                            border: "1px solid #E5E7EB",
                            "&:hover": {
                                backgroundColor: "#F3F4F6",
                                borderColor: "#D1D5DB",
                            },
                        }}
                    /> */}
                    <Button onClick={handleClose}
                        sx={{
                            textTransform: "none",
                            color: "#F15744",
                            fontSize: "14px",
                            lineHeight: "100%",
                            fontFamily: "Outfit, sans-serif",
                            borderRadius: "25px",
                            fontWeight: 500,
                            px: "20px",
                            py: "15px",
                        }}>
                        Cancel
                    </Button>
                    {/* <CustomButton
                        label={isLoading ? "Uploading..." : "Upload"}
                        type="submit"
                        disabled={isLoading || !formik.values.file}
                        sx={{
                            backgroundColor: "#00344C",
                            color: "#fff",
                            px: 4,
                            "&:hover": { backgroundColor: "#002639" },
                        }}
                    /> */}
                    <Button
                        type="submit"
                        variant="contained"
                        loading={isLoading}
                        sx={{
                            backgroundColor: "#00344C",
                            borderRadius: "25px",
                            textTransform: "none",
                            fontWeight: 500,
                            fontSize: "14px",
                            lineHeight: "100%",
                            fontFamily: "Outfit, sans-serif",
                            color: "#FFFFFF",
                            px: "20px",
                            py: "15px",
                        }}
                        disabled={isLoading}
                    >
                        Save
                    </Button>
                </Box>
            </form>
        </DialogComponent>
    );
};

export default UploadFile;
